import {
  Box,
  HStack,
  MenuItem,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {HEADER_HEIGHT} from '@/constants/layout';
import {arePeriodesEqual} from '../utils/dateRangeUtils';
import {useTranslation} from 'react-i18next';
import {useCallback, useEffect, useState, useTransition} from 'react';
import {useHalCollection} from '@synako/haligator';
import {useListes} from '../../../haligator-factory';
import {MonthSelector} from './MonthSelector';
import {DateRangeSelectorButton} from './DateRangeSelectorButton';
import {DateRangeTag} from './DateRangeTag';
import {FilterButton} from './FilterButton';
import {ProduitsSaisonniers} from './produits-par-saison/LigneProduitsSaisonniers';
import {Destination} from '../context/ListItemsContext';
import {Navigable} from '@synako/halitrek';
import {State} from '@synako/halitrek';
import {ListeDesc} from 'api-types/ffconnect-hal';
import {WhiteButton} from '@/components/Button/WhiteButton';
import {CartIcon} from '@/components/svg/CartIcon';
import {ErrorBoundary} from 'react-error-boundary';
import {CadencierIcon} from '@/components/svg/CadencierIcon';
import {ListIcon} from '@/components/svg/ListIcon';

export interface Periode {
  date_debut: string;
  date_fin: string;
}

// Types de sélection de périodes
type PeriodeSelectionType = 'month' | 'custom';

// Composant qui calcule et affiche le nombre d'items d'une liste
// extrait pour éviter le remontage à chaque render du parent
const NombreProduitsListe: React.FC<{liste: State<ListeDesc>}> = ({liste}) => {
  const {t} = useTranslation();
  const items = liste.followAllOrThrow('selection');
  return (
    <Text
      ml={2}
      fontSize='xs'
      color='gray.500'
    >
      {t('global.produit', {count: items.length})}
    </Text>
  );
};

// Vue des produits commandés par saison
export const ProduitsParSaison: React.FC = () => {
  const {t} = useTranslation();
  const [selectedPeriod, setSelectedPeriod] = useState<Periode | undefined>(
    undefined,
  );
  const [customDateRange, setCustomDateRange] = useState<Periode | null>(null);
  const [selectionType, setSelectionType] =
    useState<PeriodeSelectionType>('month');
  const [selectedListe, setSelectedListe] = useState<
    Navigable<ListeDesc> | undefined
  >(undefined);
  const [selectedListeName, setSelectedListeName] = useState<string>('');
  // Destination de l'ajout des produits : panier (défaut), cadencier ou une liste
  const [destination, setDestination] = useState<Destination>('panier');
  const [, startTransition] = useTransition();
  const {items: listes, refreshing, loading} = useHalCollection(useListes());

  const [itemCount, setItemCount] = useState<number>(0);
  const [somethingRefreshing, setSomethingRefreshing] =
    useState<boolean>(false);

  // Gère les changements de période sélectionnée via MonthSelector
  const handlePeriodChange = useCallback(
    (period: Periode | undefined) => {
      setSelectedPeriod(period);
      // Si une plage de dates personnalisée était sélectionnée, la désactiver
      if (customDateRange) {
        setCustomDateRange(null);
        setSelectionType('month');
      }
    },
    [customDateRange],
  );

  // Gère les changements de plage de dates personnalisée
  const handleDateRangeChange = useCallback((dateRange: Periode | null) => {
    setCustomDateRange(dateRange);
    if (dateRange) {
      setSelectedPeriod(dateRange);
      setSelectionType('custom');
    } else {
      // Si on supprime la plage personnalisée, revenir à la sélection par mois
      setSelectionType('month');
    }
  }, []);

  // Effet pour mettre à jour la période sélectionnée quand le type de sélection change
  useEffect(() => {
    if (
      selectionType === 'custom' &&
      customDateRange &&
      !arePeriodesEqual(selectedPeriod, customDateRange)
    ) {
      setSelectedPeriod(customDateRange);
    }
  }, [selectionType, customDateRange, selectedPeriod]);

  return (
    <VStack
      align='stretch'
      spacing={4}
      width='100%'
      py='20px'
    >
      {/* Barre Période + mois — sticky sous la navbar */}
      <HStack
        w='100%'
        px={{base: '2', md: '4', lg: '6'}}
        pt='0'
        gap={2}
        position='sticky'
        top={HEADER_HEIGHT}
        zIndex='dropdown'
        bg='white'
      >
        <DateRangeSelectorButton
          selectedDateRange={customDateRange}
          onDateRangeChange={handleDateRangeChange}
        />
        <Box
          flex='1'
          minW={0}
          position='relative'
        >
          <MonthSelector
            selectedPeriod={selectedPeriod}
            onPeriodChange={handlePeriodChange}
            isDisabled={selectionType === 'custom'}
          />
        </Box>
      </HStack>

      {/* Affichage du tag de plage de dates en dessous */}
      {customDateRange && selectionType === 'custom' && (
        <Box pb={2}>
          <DateRangeTag
            dateRange={customDateRange}
            onDateRangeChange={handleDateRangeChange}
          />
        </Box>
      )}

      {/* En md+ : badge + boutons sur une ligne. En base : badge, texte, boutons sur 3 lignes */}
      <Box
        display='flex'
        flexDirection={{base: 'column', md: 'row'}}
        justifyContent={{md: 'space-between'}}
        alignItems={{base: 'flex-start', md: 'center'}}
        px={{base: '2', md: '4', lg: '6'}}
        gap={2}
      >
        {/* Nombre de produits */}
        <HStack spacing={2}>
          <Box
            bg='gray.light_bg'
            borderRadius='8px'
            px='4'
            py='2'
          >
            <Text
              fontSize='sm'
              color='gray.dark'
              fontWeight='500'
            >
              {t('global.produit', {count: itemCount})}
            </Text>
          </Box>
          {somethingRefreshing && <Spinner size='sm' />}
        </HStack>
        {/* Actions : Ajouter les produits à ... */}
        <HStack
          spacing={3}
          flexWrap={{base: 'wrap', md: 'nowrap'}}
        >
          <Text
            fontSize='sm'
            color='black.text'
            fontWeight='700'
            whiteSpace='nowrap'
          >
            {t('liste.ajouter_produits')}
          </Text>
          {/* Bouton Cadencier — texte masqué en base, tooltip en base uniquement */}
          <Tooltip
            label={t('liste.tooltip_ajouter_cadencier')}
            hasArrow
            display={{base: 'block', md: 'none'}}
          >
            <Box>
              <WhiteButton
                texte={
                  <Box display={{base: 'none', md: 'inline'}}>
                    {t('global.cadencier')}
                  </Box>
                }
                leftIcon={
                  <CadencierIcon
                    height='5'
                    width='5'
                    color='currentColor'
                  />
                }
                pl={{base: '20px', md: '10px'}}
                borderRadius='8px'
                h='38px'
                w={{base: 'auto', md: '110px'}}
                onClick={() => {
                  startTransition(() => {
                    setDestination('cadencier');
                    setSelectedListe(undefined);
                  });
                }}
                color={destination === 'cadencier' ? 'blue.main' : 'gray.dark'}
                fontWeight={destination === 'cadencier' ? '700' : '400'}
                sx={{
                  borderColor:
                    destination === 'cadencier'
                      ? 'var(--chakra-colors-blue-main)'
                      : 'var(--chakra-colors-gray-light)',
                }}
              />
            </Box>
          </Tooltip>
          {/* Bouton Panier */}
          <WhiteButton
            texte={t('panier.panier')}
            leftIcon={
              <CartIcon
                width='16px'
                height='16px'
                color='currentColor'
              />
            }
            borderRadius='8px'
            h='38px'
            w='95px'
            onClick={() => {
              startTransition(() => {
                setDestination('panier');
                setSelectedListe(undefined);
              });
            }}
            color={destination === 'panier' ? 'blue.main' : 'gray.dark'}
            fontWeight={destination === 'panier' ? '700' : '400'}
            sx={{
              borderColor:
                destination === 'panier'
                  ? 'var(--chakra-colors-blue-main)'
                  : 'var(--chakra-colors-gray-light)',
            }}
          />
          {/* Dropdown Choisir une liste */}
          <FilterButton
            label={
              selectedListe ? selectedListeName : t('liste.choisir_une_liste')
            }
            leftIcon={
              <ListIcon
                color='currentColor'
                width='16px'
              />
            }
            bg='white'
            textColor={destination === 'liste' ? 'blue.main' : 'gray.dark'}
            chevronColor={destination === 'liste' ? 'blue.main' : 'gray.dark'}
            borderRadius={8}
            border='1px solid'
            borderColor={destination === 'liste' ? 'blue.main' : 'gray.light'}
            w='200px'
            minW='200px'
            py='4'
            h='38px'
            fontWeight={destination === 'liste' ? '700' : '400'}
            _hover={{bg: 'blue.dark', borderColor: 'white', color: 'white'}}
          >
            {loading || refreshing ? (
              <MenuItem
                disabled
                opacity={0.5}
              >
                {t('liste.chargement_listes')}
              </MenuItem>
            ) : (
              listes?.map((liste) => (
                <MenuItem
                  key={liste.data.id}
                  onClick={() => {
                    startTransition(() => {
                      setDestination('liste');
                      setSelectedListe(liste);
                      setSelectedListeName(liste.data.nom_liste);
                    });
                  }}
                >
                  <Text>{liste.data.nom_liste}</Text>
                  <ErrorBoundary fallbackRender={() => null}>
                    <NombreProduitsListe liste={liste} />
                  </ErrorBoundary>
                </MenuItem>
              ))
            )}
          </FilterButton>
        </HStack>
      </Box>

      {selectedPeriod && (
        <Box
          mt={4}
          mx={{base: '2', md: '4', lg: '6'}}
          border={somethingRefreshing ? 'none' : '1px solid'}
          borderColor='gray.light'
          borderRadius='12px'
          boxShadow={somethingRefreshing ? 'none' : 'md'}
          overflow='hidden'
        >
          <ProduitsSaisonniers
            selectedPeriod={selectedPeriod}
            selectedListe={selectedListe}
            destination={destination} // Indique la destination d'ajout des produits (panier, cadencier ou liste)
            setItemCount={setItemCount}
            setSomethingRefreshing={setSomethingRefreshing}
          />
        </Box>
      )}
    </VStack>
  );
};
